import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { insightsAPI, categoriesAPI } from '../../services/api';
import Toast from '../../components/common/Toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MediaPicker from '../../components/MediaPicker'; 

const InsightForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [showMediaPicker, setShowMediaPicker] = useState(false); 
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const [formData, setFormData] = useState({
        title: '',
        category_id: '',
        image_url: '',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        fetchCategories();
        if (id) fetchInsight();
    }, [id]);

    const fetchCategories = async () => {
        try {
            const response = await categoriesAPI.getGuidanceCategories();
            if (response.data.success) {
                setCategories(response.data.data.categories.filter(c => c.id !== 'all'));
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchInsight = async () => {
        setLoading(true);
        try {
            const response = await insightsAPI.getById(id);
            if (response.data.success) {
                const insight = response.data.data;
                setFormData({
                    ...insight,
                    is_active: Number(insight.is_active) === 1,
                });
            }
        } catch (error) {
            console.error('Failed to fetch insight:', error);
            setToast({ show: true, message: 'Failed to load insight', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (isEdit) {
                await insightsAPI.update(id, formData);
                setToast({ show: true, message: 'Insight updated successfully', type: 'success' });
            } else {
                await insightsAPI.create(formData);
                setToast({ show: true, message: 'Insight created successfully', type: 'success' });
            }
            setTimeout(() => navigate('/insights'), 1500);
        } catch (error) {
            setToast({ show: true, message: error.response?.data?.message || 'Failed to save insight', type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading insight..." />;
    }

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ ...toast, show: false })}
            />

            <div className="mb-4">
                <button className="btn btn-outline-secondary" onClick={() => navigate('/insights')}>
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Insights
                </button>
            </div>

            <div className="stat-card">
                <h4 className="mb-4">{isEdit ? 'Edit' : 'Create'} Daily Insight</h4>

                <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                        {/* Title */}
                        <div className="col-12">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </div>

                        {/* Category */}
                        <div className="col-md-6">
                            <label className="form-label">Category *</label>
                            <select
                                className="form-select"
                                value={formData.category_id}
                                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Display Order */}
                        <div className="col-md-6">
                            <label className="form-label">Display Order</label>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                min="0"
                            />
                        </div>

                        {/*  INSIGHT IMAGE - MEDIA PICKER (REPLACED FILE UPLOAD) */}
                        <div className="col-12">
                            <label className="form-label">Insight Image</label>
                            <div>
                                {formData.image_url && (
                                    <div className="mb-2 position-relative" style={{ maxWidth: '200px' }}>
                                        <img 
                                            src={formData.image_url} 
                                            alt="Preview" 
                                            className="w-100 rounded" 
                                            style={{ height: '150px', objectFit: 'cover' }} 
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                            onClick={() => setFormData({ ...formData, image_url: '' })}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => setShowMediaPicker(true)}
                                >
                                    {formData.image_url ? 'Change Image' : 'Select Image'}
                                </button>
                            </div>
                        </div>

                        {/* Active Checkbox */}
                        <div className="col-12">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="is_active">
                                    Active
                                </label>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="col-12">
                            <div className="d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-save me-2"></i>
                                            {isEdit ? 'Update Insight' : 'Create Insight'}
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/insights')}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* MEDIA PICKER MODAL */}
            <MediaPicker
                show={showMediaPicker}
                onClose={() => setShowMediaPicker(false)}
                onSelect={(url) => setFormData({ ...formData, image_url: url })}
            />
        </div>
    );
};

export default InsightForm;